import type React from "react";

import { dashed } from "../../../lib/utils/strings";
import { FieldLabel } from "../../FieldLabel";

import { WarningIcon } from "./WarningIcon";

export interface FormFieldLayoutProps {
  fieldId: string; // should be camelCase
  label: React.ReactNode;
  isRequired: boolean;
  showPlaceholderAndHideLabel?: boolean;
  errorMessage?: React.ReactNode;
  warningMessage?: React.ReactNode;
  helperMessage?: React.ReactNode;
  children?: React.ReactNode;
  explanation?: React.ReactNode;
  containerId?: string;
}

export const getFormFieldLabelHtmlId = (fieldId: string) => `sid-${dashed(fieldId)}-label`;
export const getFormFieldErrorHtmlId = (fieldId: string) => `sid-${dashed(fieldId)}-error`;
export const getFormFieldWarningHtmlId = (fieldId: string) => `sid-${dashed(fieldId)}-warning`;
export const getFormFieldHelperHtmlId = (fieldId: string) => `sid-${dashed(fieldId)}-helper`;

/**
 * In the JSLIB, a FormField has a label, inline error message, and children like a text input
 * This component provides consistent structure, classnames and some accessibility considerations for all FormFields
 */
export const FormFieldLayout = ({
  fieldId,
  label,
  errorMessage,
  warningMessage,
  helperMessage,
  isRequired,
  showPlaceholderAndHideLabel = false,
  children,
  explanation = undefined,
  containerId = undefined,
}: FormFieldLayoutProps) => {
  const labelHtmlId = getFormFieldLabelHtmlId(fieldId);
  const errorHtmlId = getFormFieldErrorHtmlId(fieldId);
  const warningHtmlId = getFormFieldWarningHtmlId(fieldId);
  const helperHtmlId = getFormFieldHelperHtmlId(fieldId);
  const containerClassName = containerId || `sid-${dashed(fieldId)}`;

  return (
    <div className={`sid-field ${containerClassName}`}>
      <div className="sid-l-space-top-md" />

      <FieldLabel
        text={label}
        isRequired={isRequired}
        htmlForLabel={`sid-${dashed(fieldId)}`}
        id={labelHtmlId}
        displayClasses={`${explanation ? "sid-field__label-with-explanation" : ""}${
          explanation && showPlaceholderAndHideLabel ? " " : ""
        }${showPlaceholderAndHideLabel ? "sid-h-screen-reader-only" : ""}`}
      >
        {explanation && explanation}
      </FieldLabel>

      {children}

      {errorMessage && (
        <div className="sid-field-error" id={errorHtmlId}>
          {errorMessage}
        </div>
      )}

      {!errorMessage && warningMessage && (
        <div className="sid-field-warning" id={warningHtmlId}>
          <WarningIcon className="sid-field-warning__icon" />
          <div className="sid-field-warning__message">{warningMessage}</div>
        </div>
      )}

      {!errorMessage && !warningMessage && helperMessage && (
        <div className="sid-field-helper" id={helperHtmlId}>
          {helperMessage}
        </div>
      )}
    </div>
  );
};
